import React from 'react';
import Step from './Step';
import ExpandableContainer from './ExpandableContainer';
import HeatExchangerRequirements from './HeatExchangerRequirements'
import * as Row from '../../layout/Row';
import Field from '../../../model/configurator/Field';
import StepModel from '../../../model/configurator/StepModel';
import QuickStepSetupField from '../../../model/configurator/heating/QuickStepSetupField';
import HeatExchangerField from '../../../model/configurator/heating/HeatExchangerField';
import {GetPumpLiftingHeightTips} from './helpers/heatingHelpers';

export default class StepHeatingTWS extends Step {
    renderBody(model) {
        return (
            <React.Fragment>
                <div className="row row-no-outer-gutter flex-center-v spaced-items-group-xs"
                     style={{marginBottom: '1rem'}}>
                    <div className="spaced-item column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.capacity)}
                    </div>
                    <div className="spaced-item column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.quickStepSetup)}
                    </div>
                </div>
                {this.renderHeatExchangerRequirements(model)}
                {this.renderTanks()}
                {this.renderSecondarySideComponents()}
                {this.renderPumps()}
            </React.Fragment>
        )
    }

    renderHeatExchangerRequirements(model) {
        return (
            <HeatExchangerRequirements
                model={model.heatingTWS}
                primary={this.fields.primary}
                secondary={this.fields.secondary}
                plateType={this.fields.plateType}
                numberOfPlates={this.fields.numberOfPlates}
                renderField={this.renderFieldv2}>
                <div className="row row-no-outer-gutter">
                    <div className="column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.heatExchanger)}
                    </div>
                </div>
            </HeatExchangerRequirements>
        )
    }

    renderTanks() {
        const fields = [this.fields.tank];
        if (!this.renderSection(...fields)) {
            return <React.Fragment/>
        }
        const warnings = [fields.map(x => x.warning)];
        return (
            <ExpandableContainer warnings={warnings} label={__('Tanks')}>
                <Row.Container hasOuterGutter={false}>
                    <div className="column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.tank)}
                        {this.renderGenericHorizontalField(this.fields.tankInsulation)}
                        {this.renderGenericHorizontalField(this.fields.tankHeater)}
                    </div>
                </Row.Container>
            </ExpandableContainer>
        )
    }

    renderSecondarySideComponents() {
        const fields = [this.fields.controlValve, this.fields.primaryActuator, this.fields.secondaryActuator];
        if (!this.renderSection(...fields)) {
            return <React.Fragment/>
        }
        const warnings = [fields.map(x => x.warning)];
        return (
            <ExpandableContainer warnings={warnings} label={__('Secondary side components')}>
                <Row.Container hasOuterGutter={false}>
                    <div className="column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.controlValve)}
                        {this.renderGenericHorizontalField(this.fields.primaryActuator)}
                        {this.renderGenericHorizontalField(this.fields.secondaryActuator)}
                    </div>
                </Row.Container>
            </ExpandableContainer>
        )
    }

    renderPumps() {
        const fields = [this.fields.primaryPump, this.fields.secondaryPump, this.fields.circulationPump];
        if (!this.renderSection(...fields)) {
            return <React.Fragment/>
        }
        const warnings = [fields.map(x => x.warning)];
        return (
            <ExpandableContainer warnings={warnings} label={__('Pumps')}>
                <Row.Container hasOuterGutter={false}>
                    <div className="column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.primaryPump, {}, GetPumpLiftingHeightTips(this.fields.primaryPumpData))}
                        {this.renderGenericHorizontalField(this.fields.secondaryPump, {}, GetPumpLiftingHeightTips(this.fields.primaryPumpData))}
                        {this.renderGenericHorizontalField(this.fields.circulationPump, {}, GetPumpLiftingHeightTips(this.fields.primaryPumpData))}
                    </div>
                </Row.Container>
            </ExpandableContainer>
        )
    }

    static getFields(step) {
        const createField = name => new Field(name, step)
        const createExchangerSide = name => {
            return {
                inletTemperature: new HeatExchangerField(name + '.inletTemperature', step),
                outletTemperature: new HeatExchangerField(name + '.outletTemperature', step),
                fluid: new HeatExchangerField(name + '.fluid', step),
                flow: new HeatExchangerField(name + '.flow', step),
                maxPressureDrop: new HeatExchangerField(name + '.maxPressureDrop', step),
            }
        }

        const pumpData = name => {
            return {
                pumpFlow: createField(`${name}.pumpFlow`),
                pumpLiftingHeight: createField(`${name}.pumpLiftingHeight`),
                pumpVoltage: createField(`${name}.pumpVoltage`),
                pumpPowerSupply: createField(`${name}.pumpPowerSupply`),
                maxPower: createField(`${name}.maxPower`),
                maxCurrent: createField(`${name}.maxCurrent`),
            }
        }

        return {
            capacity: createField('capacity'),
            quickStepSetup: new QuickStepSetupField('quickStepSetup', step),
            primary: createExchangerSide('primary'),
            secondary: createExchangerSide('secondary'),
            heatExchanger: createField('heatExchanger'),
            tank: createField('tank'),
            tankHeater: createField('tankHeater'),
            tankInsulation: createField('tankInsulation'),
            plateType: createField('plateType'),
            numberOfPlates: createField('numberOfPlates'),
            controlValve: createField('controlValve'),
            primaryActuator: createField('primaryActuator'),
            secondaryActuator: createField('secondaryActuator'),
            primaryPump: createField('primaryPump'),
            primaryPumpData: pumpData('primaryPumpData'),
            secondaryPump: createField('secondaryPump'),
            secondaryPumpData: pumpData('secondaryPumpData'),
            circulationPump: createField('circulationPump'),
            circulationPumpData: pumpData('circulationPumpData'),
            temperature: new Field('design.temperature', step, false),
            pressure: new Field('design.pressure', step, false),
            pn: createField('design.pn')
        }
    }

    static getStepModel(model) {
        return new StepModel('heatingTWS', model.heatingTWS)
    }
}